/**
 * The ONLY timing values in this layer.
 *
 * These are a retry POLICY, not an estimate of how long the backend takes. A
 * fixed ladder (which this replaces) encodes a belief — "the indexer is done by
 * 10s" — and fires every rung whether the data arrived at 400ms or never. This
 * schedule encodes no belief about when data arrives. It bounds only how often
 * we are willing to ask and how long we keep asking; termination is decided
 * solely by observed evidence, so a server that answers immediately costs one
 * request. The clock's only role is the negative one: after the budget we stop
 * asking and say we do not know, rather than presenting the last answer as if
 * it were current.
 */
export const RETRY_POLICY = Object.freeze({
  /** Roughly one round trip plus commit — a floor on politeness, not a claim. */
  baseMs: 350,
  /** 2.0 reaches the cap in four steps and wastes budget sitting there; 1.5 spends too long under a second. */
  factor: 1.8,
  /** Past ~4s of silence the trader has already noticed; keeps late attempts responsive. */
  capMs: 4000,
  /** Several targets converge on one action; without jitter they fire in the same millisecond. */
  jitterRatio: 0.2,
  /** Whole per-target budget. Worst case here is cheaper than the old best case. */
  maxAttempts: 7,
  /** Independent of attempt count, so slow fetches cannot stretch a run indefinitely. */
  maxWallClockMs: 20000,
  /** Repeated failures mean a truthful message beats spending the rest of the budget. */
  maxConsecutiveErrors: 3,
});

/**
 * Delay BEFORE the given attempt. Attempt 0 runs immediately; each later delay
 * is measured from when the previous attempt SETTLED, so requests cannot pile
 * up against a slow backend the way a fire-everything-from-t0 ladder does.
 */
export const nextDelayMs = (
  attempt: number,
  random: () => number = Math.random
): number => {
  if (attempt <= 0) return 0;

  const raw = RETRY_POLICY.baseMs * RETRY_POLICY.factor ** (attempt - 1);
  const capped = Math.min(raw, RETRY_POLICY.capMs);
  // Symmetric jitter around the nominal delay.
  const jitter = capped * RETRY_POLICY.jitterRatio * (random() * 2 - 1);
  return Math.max(0, Math.round(capped + jitter));
};

/** Nominal (un-jittered) total of every delay in the budget. */
export const nominalBudgetMs = (): number => {
  let total = 0;
  for (let attempt = 1; attempt < RETRY_POLICY.maxAttempts; attempt += 1) {
    total += Math.min(
      RETRY_POLICY.baseMs * RETRY_POLICY.factor ** (attempt - 1),
      RETRY_POLICY.capMs
    );
  }
  return total;
};
